import { gameView } from '../../../packages/domain/src/50-game-engine.mjs';
import { paintGameBoard } from './35-game-board.mjs';

const NS = 'http://www.w3.org/2000/svg';
/** Bubble Grove presentation follows accepted clear cells and exact column
 * gravity. Shards belong to outgoing bubbles; refill pieces never inherit their
 * burst. Animation owns no random draws, moves, rewards or scoring authority. */
export function createClusterPlayback(ctx, board, { onClear, settle } = {}) {
  let generation = 0,
    active = null,
    disposed = false;
  const animations = new Set(),
    phases = new Set(),
    effects = new Set();
  const media = window.matchMedia('(prefers-reduced-motion: reduce)');
  function cancel() {
    generation++;
    active = null;
    for (const animation of animations) animation.cancel();
    for (const effect of effects) effect.remove();
    animations.clear();
    phases.clear();
    effects.clear();
    delete board.dataset.clusterPhase;
    delete board.dataset.clusterIntensity;
    for (const cell of board.children) delete cell.dataset.clusterOrigin;
  }
  function paint(state) {
    paintGameBoard(ctx, board, state, () => {}, { disabled: true, motion: false });
    for (const effect of effects) board.append(effect);
  }
  function finishSuspended() {
    const current = active;
    if (!current || (!document.hidden && !media.matches)) return;
    cancel();
    if (board.isConnected) paint(current.next);
    current.settle?.(current.next);
  }
  document.addEventListener?.('visibilitychange', finishSuspended);
  media.addEventListener?.('change', finishSuspended);
  function animate(
    node,
    frames,
    duration,
    { decorative = false, easing = 'ease-out', delay = 0 } = {},
  ) {
    if (typeof node?.animate !== 'function') return;
    // Backfill delayed starts so newly painted refill cells never flash in their
    // final position while the neighbouring column is still falling.
    const animation = node.animate(frames, { duration, delay, fill: 'both', easing });
    animations.add(animation);
    if (!decorative) phases.add(animation);
  }
  async function wait() {
    const batch = [...phases];
    await Promise.all(batch.map((animation) => animation.finished.catch(() => {})));
    for (const animation of batch) {
      animation.cancel();
      animations.delete(animation);
      phases.delete(animation);
    }
  }
  function shape(parent, tag, attributes, text = '') {
    const node =
      typeof document.createElementNS === 'function'
        ? document.createElementNS(NS, tag)
        : ctx.e(tag);
    for (const [key, value] of Object.entries(attributes)) node.setAttribute(key, String(value));
    if (text) node.textContent = text;
    parent.append(node);
    return node;
  }
  function burst(previous, clear) {
    board.dataset.clusterPhase = 'pop';
    const intensity = Math.min(3, Math.max(clear.combo || 0, clear.cells.length >= 10 ? 2 : 1));
    board.dataset.clusterIntensity = String(intensity);
    const origin = board.getBoundingClientRect();
    const overlay = shape(board, 'svg', {
      viewBox: `0 0 ${origin.width} ${origin.height}`,
      'aria-hidden': 'true',
    });
    Object.assign(overlay.style, {
      position: 'absolute',
      inset: '0',
      width: '100%',
      height: '100%',
      overflow: 'visible',
      pointerEvents: 'none',
      zIndex: '5',
    });
    effects.add(overlay);
    const source = board.children[clear.cells[0]].getBoundingClientRect();
    const sourceX = source.left + source.width / 2,
      sourceY = source.top + source.height / 2;
    const particleStride = Math.max(1, Math.ceil(clear.cells.length / 24));
    let centreX = 0,
      centreY = 0;
    for (const [position, index] of clear.cells.entries()) {
      const cell = board.children[index],
        rect = cell.getBoundingClientRect();
      const x = rect.left - origin.left + rect.width / 2,
        y = rect.top - origin.top + rect.height / 2;
      const colour = `var(--game-colour-${previous.data.board[index]})`;
      const delay = Math.min(
        64,
        (Math.hypot(rect.left + rect.width / 2 - sourceX, rect.top + rect.height / 2 - sourceY) /
          Math.max(1, rect.width)) *
          7,
      );
      centreX += x;
      centreY += y;
      // Keep source identity but bound expensive particle effects on dense clears.
      if (position % particleStride === 0) {
        const ring = shape(overlay, 'circle', {
          cx: x,
          cy: y,
          r: rect.width * 0.34,
          fill: 'none',
          stroke: colour,
          'stroke-width': 3,
        });
        ring.style.transformOrigin = `${x}px ${y}px`;
        animate(
          ring,
          [
            { opacity: 0.9, transform: 'scale(.6)' },
            { opacity: 0, transform: 'scale(1.6)' },
          ],
          300,
          { decorative: true, delay },
        );
        for (let shard = 0; shard < 4; shard++) {
          const angle = (shard * Math.PI) / 2 + Math.PI / 4;
          const dx = Math.cos(angle) * rect.width * 0.6,
            dy = Math.sin(angle) * rect.height * 0.6;
          const node = shape(overlay, 'path', {
            d: `M${x} ${y - 3}L${x + 3} ${y}L${x} ${y + 3}L${x - 3} ${y}Z`,
            fill: colour,
            stroke: 'var(--game-white)',
            'stroke-width': 0.8,
          });
          node.dataset.sourceCell = String(index);
          node.dataset.sourceColour = String(previous.data.board[index]);
          node.style.transformOrigin = `${x}px ${y}px`;
          animate(
            node,
            [
              { opacity: 1, transform: 'translate(0,0) scale(1)' },
              { opacity: 0, transform: `translate(${dx}px,${dy}px) scale(.3)` },
            ],
            380,
            { decorative: true, delay },
          );
        }
      }
      animate(
        cell,
        [
          { opacity: 1, transform: 'scale(1)' },
          { opacity: 1, transform: 'scale(1.16)', offset: 0.32 },
          { opacity: 0, transform: 'scale(.12)' },
        ],
        220,
        { easing: 'cubic-bezier(.3,0,.7,1)', delay },
      );
    }
    const x = centreX / clear.cells.length,
      y = centreY / clear.cells.length;
    if (intensity > 1) {
      const impact = shape(overlay, 'circle', {
        cx: x,
        cy: y,
        r: Math.max(source.width, Math.min(origin.width * 0.2, source.width * 2.5)),
        fill: 'none',
        stroke: 'var(--game-colour-3)',
        'stroke-width': intensity === 3 ? 4 : 2,
        class: 'game-cluster-impact',
        'data-earned-intensity': intensity,
      });
      impact.style.transformOrigin = `${x}px ${y}px`;
      animate(
        impact,
        [
          { opacity: 0, transform: 'scale(.25)' },
          { opacity: 0.85, transform: 'scale(.7)', offset: 0.2 },
          { opacity: 0, transform: 'scale(1.8)' },
        ],
        430,
        { decorative: true },
      );
    }
    if (clear.cells.length >= 10 || clear.combo >= 3) {
      const sprite = ctx.e('span', 'game-cluster-sprite');
      sprite.setAttribute('aria-hidden', 'true');
      const size = Math.min(origin.width * 0.65, Math.max(90, source.width * 5));
      Object.assign(sprite.style, {
        left: `${Math.max(size / 2, Math.min(origin.width - size / 2, x)) - size / 2}px`,
        top: `${Math.max(size / 2, Math.min(origin.height - size / 2, y)) - size / 2}px`,
        width: `${size}px`,
        height: `${size}px`,
      });
      board.append(sprite);
      effects.add(sprite);
      animate(
        sprite,
        Array.from({ length: 10 }, (_, frame) => ({
          backgroundPosition: `${(Math.min(frame, 8) % 3) * 50}% ${Math.floor(Math.min(frame, 8) / 3) * 50}%`,
          opacity: frame === 9 ? 0 : 1,
          offset: frame / 9,
        })),
        510,
        { decorative: true, easing: 'steps(1, end)' },
      );
    }
    const award = shape(
      overlay,
      'text',
      {
        x: Math.max(70, Math.min(origin.width - 70, x)),
        y: Math.max(22, Math.min(origin.height - 22, y)),
        class: 'game-cluster-award',
        'text-anchor': 'middle',
        'data-source-size': clear.cells.length,
      },
      `+${clear.points || 0}`,
    );
    animate(
      award,
      [
        { opacity: 0, transform: 'translateY(5px)' },
        { opacity: 1, transform: 'translateY(-3px)', offset: 0.25 },
        { opacity: 0, transform: 'translateY(-22px)' },
      ],
      540,
      { decorative: true },
    );
    if (clear.combo > 1) {
      const streak = shape(
        overlay,
        'text',
        {
          x: Math.max(60, Math.min(origin.width - 60, x)),
          y: Math.max(42, Math.min(origin.height - 8, y + 23)),
          class: 'game-cluster-streak game-cluster-combo',
          'text-anchor': 'middle',
          'data-earned-combo': clear.combo,
        },
        `COMBO ${clear.combo}`,
      );
      animate(
        streak,
        [
          { opacity: 0, transform: 'translateY(7px) scale(.85)' },
          { opacity: 1, transform: 'translateY(0) scale(1.06)', offset: 0.3 },
          { opacity: 0, transform: 'translateY(-12px) scale(1)' },
        ],
        540,
        { decorative: true },
      );
    }
  }

  function drop(previous, next, gravity, artwork) {
    board.dataset.clusterPhase = 'fall';
    const columns = gameView(previous).columns;
    const first = board.children[0].getBoundingClientRect();
    const stride = board.children[columns].getBoundingClientRect().top - first.top || first.height;
    const movements = new Map(gravity.movements.map(([from, to]) => [to, from]));
    const refills = new Set(gravity.refills.map(([index]) => index));
    const refillDepths = new Map();
    for (const [index] of gravity.refills)
      refillDepths.set(index % columns, (refillDepths.get(index % columns) || 0) + 1);
    const rows = next.data.board.length / columns;
    for (let target = 0; target < next.data.board.length; target++) {
      const node = board.children[target];
      const from = movements.get(target) ?? target;
      const refill = refills.has(target);
      // Surviving artwork follows its accepted original identity, including
      // unchanged columns. A malformed transcript never invents a survivor.
      if (!refill && previous.data.board[from] === next.data.board[target] && artwork[from])
        node.replaceChildren(artwork[from]);
      if ((!refill && from === target) || next.data.board[target] === null) continue;
      if (!refill && previous.data.board[from] !== next.data.board[target]) continue;
      const row = Math.floor(target / columns);
      const depth = refill ? refillDepths.get(target % columns) : row - Math.floor(from / columns);
      const distance = -depth * stride;
      node.dataset.clusterOrigin = refill ? 'refill' : String(from);
      // All pieces in a column travel together. New rows enter from real
      // off-board positions; a subpixel settle avoids a dense-grid trampoline.
      animate(
        node,
        [
          { opacity: refill ? 0 : 1, transform: `translateY(${distance}px)` },
          { opacity: 1, offset: 0.18 },
          { opacity: 1, transform: `translateY(${Math.min(1.2, stride * 0.035)}px)`, offset: 0.9 },
          { opacity: 1, transform: 'translateY(0)' },
        ],
        Math.min(420, 310 + Math.min(rows, depth) * 8),
        { easing: 'cubic-bezier(.32,0,.38,1)', delay: Math.min(54, (target % columns) * 4) },
      );
    }
  }

  function play(previous, next, events, settleOverride) {
    cancel();
    const clear = events.find((event) => event.type === 'clear');
    const gravity = events.find((event) => event.type === 'gravity');
    if (
      disposed ||
      next.gameId !== 'cluster-pop' ||
      !clear?.cells.length ||
      !gravity?.movements ||
      !gravity?.refills ||
      JSON.stringify(gravity.boardAfter) !== JSON.stringify(next.data.board) ||
      document.hidden ||
      media.matches ||
      !board.isConnected ||
      typeof board.children[0]?.animate !== 'function'
    )
      return null;
    const token = generation;
    active = { next, settle: settleOverride || settle };
    const current = () =>
      token === generation && board.isConnected && !document.hidden && !media.matches;
    return (async () => {
      try {
        paint(previous);
        const artwork = [...board.children].map((cell) => cell.firstChild);
        burst(previous, clear);
        onClear?.({
          ...clear,
          label: `${clear.cells.length} bubbles popped${clear.combo > 1 ? ` · Streak ${clear.combo}` : ''}`,
        });
        await wait();
        if (!current()) return;
        paint(next);
        drop(previous, next, gravity, artwork);
        await wait();
      } finally {
        if (token === generation) {
          const callback = active?.settle;
          cancel();
          if (board.isConnected) paint(next);
          callback?.(next);
        }
      }
    })();
  }
  return {
    play,
    cancel,
    get pending() {
      return active !== null;
    },
    dispose() {
      if (disposed) return;
      disposed = true;
      cancel();
      document.removeEventListener?.('visibilitychange', finishSuspended);
      media.removeEventListener?.('change', finishSuspended);
    },
  };
}
